import { supabase, uploadFile } from "libs/supabase";
import { LoadingContext } from "providers/loading";
import { FunctionComponent, useContext, useEffect, useState } from "react";
import { useId } from "react-id-generator";
import { Asset, AssetType } from "types/asset";
import Selectable from "./selectable";
import PhotoTaker from "components/dashboard/photo-taker";

interface AssetsGridProps {
  assetType: AssetType;
  selectedIds: (number | undefined)[];
  setSelectedIds: React.Dispatch<React.SetStateAction<(number | undefined)[]>>;
}

const AssetsGrid: FunctionComponent<AssetsGridProps> = ({ assetType, selectedIds, setSelectedIds }) => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const ids = useId(assets.length);
  const { setLoading } = useContext(LoadingContext);
  const [takePhotoAssetIndex, setTakePhotoAssetIndex] = useState<number>();

  useEffect(() => {
    setLoading(true);
    supabase
      .from("assets")
      .select("*")
      .eq("type", assetType)
      .then((res) => {
        setLoading(false);
        if ("data" in res) {
          setAssets(res.data as Asset[]);
        }
      });
  }, []);

  return (
    <div className="overflow-hidden">
      {takePhotoAssetIndex == null ? null : (
        <PhotoTaker
          type={assetType}
          modelUrl={assets[takePhotoAssetIndex].url}
          onClose={() => setTakePhotoAssetIndex(undefined)}
          onPhoto={async (file) => {
            const index = takePhotoAssetIndex;
            setTakePhotoAssetIndex(undefined);
            setLoading(true);
            const newImageUrl = await uploadFile("images", file);
            if (newImageUrl) {
              setAssets((prev) => {
                let arr = [...prev];
                arr[index].image_url = newImageUrl;
                return arr;
              });
              await supabase.from("assets").update({ image_url: newImageUrl }).eq("id", assets[index].id);
            }

            setLoading(false);
          }}
        />
      )}
      <div className="flex flex-wrap -m-2">
        {assets.map((asset, i) => (
          <div key={ids[i]} className="xl:w-1/5 lg:w-1/4 md:w-1/3 sm:w-1/2 w-full p-2">
            <div className="relative">
              <div className="pt-[66%]"></div>
              <div className="absolute left-0 top-0 w-full h-full rounded-[10px] overflow-hidden bg-gray-100">
                <Selectable
                  className="w-full h-full"
                  isSelected={selectedIds.includes(asset.id)}
                  onSelect={(val) => {
                    if (val) {
                      setSelectedIds((prev) => [...prev, asset.id]);
                    } else {
                      setSelectedIds((prev) => {
                        let arr = [...prev];
                        const index = arr.findIndex((id) => id === asset.id);
                        if (index !== -1) {
                          arr.splice(index, 1);
                        }
                        return arr;
                      });
                    }
                  }}
                >
                  <div
                    onClick={() => setTakePhotoAssetIndex(i)}
                    style={{ backgroundImage: `url(${asset.image_url})` }}
                    className="w-full h-full bg-contain bg-center bg-no-repeat"
                  />
                </Selectable>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AssetsGrid;
